function cleanValue(value) {
  return String(value || "").trim();
}

function cleanHostname(value) {
  const raw = cleanValue(value).toLowerCase();
  if (!raw) return "";

  try {
    const parsed = raw.includes("://") ? new URL(raw) : new URL(`https://${raw}`);
    return parsed.hostname.replace(/^www\./, "");
  } catch {
    return raw
      .replace(/^https?:\/\//, "")
      .split("/")[0]
      .split(":")[0]
      .replace(/^www\./, "");
  }
}

function readProjectFromLocation() {
  if (typeof window === "undefined") return "";

  const search = new URLSearchParams(window.location.search || "");
  const direct = search.get("project") || search.get("projectId");
  if (direct) return cleanValue(direct).toLowerCase();

  const hash = String(window.location.hash || "");
  const queryIndex = hash.indexOf("?");
  if (queryIndex < 0) return "";

  const hashSearch = new URLSearchParams(hash.slice(queryIndex + 1));
  return cleanValue(
    hashSearch.get("project") || hashSearch.get("projectId")
  ).toLowerCase();
}

/**
 * Identidad pública del storefront.
 *
 * Producción: el backend resuelve el proyecto por el dominio real del frontend.
 * Desarrollo/previsualización: se puede definir REACT_APP_PROJECT_SLUG o usar
 * ?project=slug sin modificar el código fuente.
 */
export function getStorefrontIdentity() {
  const configuredProjectKey = cleanValue(
    process.env.REACT_APP_PROJECT_SLUG
  ).toLowerCase();
  const locationProjectKey = readProjectFromLocation();
  const projectKey = configuredProjectKey || locationProjectKey;
  const storefrontHost =
    typeof window !== "undefined"
      ? cleanHostname(window.location.hostname)
      : "";
  const storefrontOrigin =
    typeof window !== "undefined" ? cleanValue(window.location.origin) : "";

  return {
    projectKey,
    storefrontHost,
    storefrontOrigin,
    resolutionMode: projectKey ? "project-key" : "domain",
  };
}

/**
 * Clave estable para aislar estado local por storefront. El hostname conserva
 * la identidad primaria de producción y el projectKey diferencia previews o
 * desarrollo cuando varias tiendas comparten el mismo origen.
 */
export function storefrontIdentityKey(identity = getStorefrontIdentity()) {
  return [identity?.storefrontHost, identity?.projectKey]
    .map((value) => cleanValue(value).toLowerCase())
    .join("::");
}

// Se conserva el export histórico como objeto compartido para consumidores que
// sólo necesitan leer la identidad. El runtime lo mantiene sincronizado ante
// cambios reales de storefront sin reemplazar su referencia.
export const storefrontIdentity = getStorefrontIdentity();

const identitySubscribers = new Set();
const IDENTITY_EVENTS = ["hashchange", "popstate", "pageshow"];
let identityListenersAttached = false;

function syncStorefrontIdentity({ notify = false } = {}) {
  const nextIdentity = getStorefrontIdentity();
  const previousKey = storefrontIdentityKey(storefrontIdentity);
  const nextKey = storefrontIdentityKey(nextIdentity);
  const changed =
    previousKey !== nextKey ||
    storefrontIdentity.storefrontOrigin !== nextIdentity.storefrontOrigin ||
    storefrontIdentity.resolutionMode !== nextIdentity.resolutionMode;

  if (!changed) return storefrontIdentity;

  Object.assign(storefrontIdentity, nextIdentity);

  if (notify) {
    identitySubscribers.forEach((listener) => {
      try {
        listener(storefrontIdentity);
      } catch (error) {
        // Un consumidor defectuoso no debe impedir que los demás sincronicen.
        console.error("[storefrontRuntime] Error al sincronizar identidad:", error);
      }
    });
  }

  return storefrontIdentity;
}

function handleStorefrontIdentityChange() {
  syncStorefrontIdentity({ notify: true });
}

function attachIdentityListeners() {
  if (
    identityListenersAttached ||
    typeof window === "undefined" ||
    typeof window.addEventListener !== "function"
  ) {
    return;
  }

  IDENTITY_EVENTS.forEach((eventName) =>
    window.addEventListener(eventName, handleStorefrontIdentityChange)
  );
  identityListenersAttached = true;
}

function detachIdentityListeners() {
  if (
    !identityListenersAttached ||
    typeof window === "undefined" ||
    typeof window.removeEventListener !== "function"
  ) {
    return;
  }

  IDENTITY_EVENTS.forEach((eventName) =>
    window.removeEventListener(eventName, handleStorefrontIdentityChange)
  );
  identityListenersAttached = false;
}

/**
 * Suscribe consumidores que deben invalidar estado cuando cambia la tienda
 * pública. No dispara al suscribirse: cada contexto obtiene su valor inicial
 * con getStorefrontIdentity/storefrontIdentityKey y sólo recibe cambios reales.
 */
export function subscribeStorefrontIdentity(listener) {
  if (typeof listener !== "function") return () => {};

  syncStorefrontIdentity();
  identitySubscribers.add(listener);
  attachIdentityListeners();

  return () => {
    identitySubscribers.delete(listener);
    if (identitySubscribers.size === 0) detachIdentityListeners();
  };
}

export function getStorefrontHeaders() {
  // Las cabeceras se calculan contra la ubicación actual para que una petición
  // nunca reutilice identidad obsoleta aunque ocurra entre eventos de navegación.
  const identity = syncStorefrontIdentity();
  const { storefrontHost, projectKey } = identity;
  const headers = {};

  if (storefrontHost) {
    headers["x-storefront-host"] = storefrontHost;
    headers["x-project-domain"] = storefrontHost;
  }

  if (projectKey) {
    headers["x-project-id"] = projectKey;
    headers["x-wp-project"] = projectKey;
  }

  return headers;
}

export { cleanHostname };
